import { firebase } from '@/shared/firebase';
import { TOKEN_STORAGE_KEY } from '@/shared/useAuth';
import { loginPathFor } from '@/shared/return-to';

/**
 * Shared Firebase-auth-token helper — the fix for the repeated `403 Firebase ID token has
 * expired` errors nexvia-api-web's logs showed against `/storage/*` routes.
 *
 * ROOT CAUSE (confirmed by reading both sides, 2026-10-14): every transport module in this repo
 * used to read a CACHED copy of the ID token straight out of `localStorage[TOKEN_STORAGE_KEY]`.
 * That copy is only ever refreshed by `useAuth.tsx`'s `onIdTokenChanged` listener, which depends
 * on Firebase's own internal background timer having fired recently — it does not fire while a
 * tab is backgrounded or the machine is asleep. A tab that had been backgrounded long enough sent
 * the exact same expired string on every one of its requests, and nothing reacted to the resulting
 * failure (a dead token just surfaced as a static error message — no retry, no forced refresh, no
 * logout).
 *
 * THE FIX: ask the live Firebase SDK for a token instead of trusting the cached copy. Plain
 * `currentUser.getIdToken()` (no force flag) already performs a real network refresh via the
 * refresh token whenever the SDK's own cached ID token is expired or within 30 seconds of expiring
 * (confirmed against the Firebase SDK's own source, `StsTokenManager.getToken`) — the same
 * mechanism nexvia-web-app's axios interceptor already relies on by asking
 * `firebase.auth().currentUser.getIdToken()` live on every outgoing request. Asking live, every
 * time, is sufficient on its own to never send an already-expired token. The localStorage mirror
 * is kept only as a fallback for the brief window before Firebase's persisted session has
 * rehydrated `currentUser` on a fresh page load (no live SDK user to ask yet) — same fallback
 * nexvia-web-app's own interceptor uses.
 *
 * GOTCHA: nexvia-api-web answers an expired-Firebase-token failure on `/storage/*` routes with
 * **403**, not 401 (confirmed against this repo's own backend logs). A caller that only reacts to
 * 401 — like a naive copy of nexvia-web-app's own response interceptor, which checks 401 only —
 * would silently never trigger for this exact, real failure. `api.ts`'s retry logic (the one
 * consumer of this module today) checks both.
 *
 * ONE SHARED HELPER, not four patches: this exact `localStorage.getItem(TOKEN_STORAGE_KEY)` read
 * is duplicated across this repo's four independent transport modules (documented as F-001 in
 * `docs/TECHNICAL-AUDIT.md` — a direct, dated consequence of this repo's concurrent-agent
 * history). Patching the read separately in each would risk the exact same drift that already
 * happened once in this repo; this module exists so every current and future transport module can
 * import the same token-and-retry logic instead of re-deriving its own copy. Only `api.ts` has
 * been wired up to it so far — the other three modules' own self-contained `request()` functions
 * are a mechanical delete-and-reimport away (see each of their own docblocks for why they are
 * self-contained today).
 */

/** Same key `useAuth.tsx`'s `onIdTokenChanged` listener writes — read here, never redefined. */
function readStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    // localStorage can be unavailable (private mode, quota) — the caller proceeds without a token
    // rather than throwing, same reasoning as every other localStorage read in this repo.
    return null;
  }
}

/**
 * The live ID token, asked straight from the Firebase SDK (see this module's own docblock for why
 * that alone fixes the stale-token bug). `forceRefresh: true` skips the SDK's own cache entirely
 * and performs a real network refresh — used by `api.ts`'s retry path after a 401/403, when the
 * token the SDK considered "not yet expired" turned out to be rejected by the backend anyway.
 *
 * Falls back to the localStorage mirror only when there is no live `currentUser` yet (the brief
 * window before Firebase's persisted session rehydrates on a fresh page load) — matching
 * nexvia-web-app's own interceptor's fallback for that same window.
 */
export async function getAuthToken(forceRefresh = false): Promise<string | null> {
  const currentUser = firebase.auth().currentUser;
  if (!currentUser) {
    return readStoredToken();
  }

  try {
    const token = await currentUser.getIdToken(forceRefresh);
    try {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch {
      /* localStorage can be unavailable (private mode, quota) — the token itself is still good */
    }
    return token;
  } catch {
    // getIdToken() can throw (e.g. a revoked refresh token) — same try/catch convention as
    // useAuth.tsx's onIdTokenChanged. No live token to hand back; api.ts's retry path treats this
    // the same as an exhausted retry and redirects to /login rather than trusting a stale mirror.
    return null;
  }
}

/**
 * Clears the cached token and sends the user back to `/login` — the one thing left to do once a
 * forced refresh has already failed to produce a token the backend accepts. A plain
 * `window.location` navigation, not the router (this is called from plain request code with no
 * component/router context to call `useNavigate` from), guarded against redirecting again when the
 * tab is already sitting on `/login`.
 *
 * NEX-1165 — the page it leaves goes along as `?returnTo=` (`return-to.ts`, same as the shell's own
 * redirect), so the new-request mail's `/solicitudes?requestId=<id>` still lands on its request
 * when the restored session is the one the API rejects.
 */
export function redirectToLogin(): void {
  if (window.location.pathname === '/login') return;

  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* localStorage can be unavailable (private mode, quota) — still navigate away below */
  }

  window.location.assign(loginPathFor(window.location.pathname, window.location.search));
}
